/* eslint-disable prefer-arrow-callback */
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { deleteSoilSightProject } from "services";

import { productAnalytics, useTranslation } from "utils";

import { LIST_SOIL_SIGHT_PROJECTS } from "consts";

import { SoilSightProject } from "models";

import { message } from "components";

import { showSoilSightSaveFailed } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";

type UseProjectDeleteWithUndoArgs = {
  klimFarmId: string;
  resolvedActiveId: string | null;
  onBeforeDelete?: () => void;
  /** Called when the deleted project was active and another project is available to switch to. */
  onSwitch: (projectId: string) => void;
  /** Called when all projects are deleted — should create and return a new blank project id. */
  onCreateBlank: (excludedProjectIds: string[]) => Promise<string | null>;
  /** Called when blank project creation fails after last project is deleted. */
  onLastDeleted?: () => void;
};

export const useProjectDeleteWithUndo = ({
  klimFarmId,
  resolvedActiveId,
  onBeforeDelete,
  onSwitch,
  onCreateBlank,
  onLastDeleted,
}: UseProjectDeleteWithUndoArgs) => {
  const { t } = useTranslation("scenarioAnalysis");
  const queryClient = useQueryClient();

  // pendingDeleteIds serves dual purpose:
  // 1. During the 5-second undo window: hides the project from the list.
  // 2. After the delete API succeeds: stays set PERMANENTLY (never removed on success) so
  //    that any stale background refetch triggered by an unrelated mutation (duplicate, create)
  //    cannot resurrect the deleted project in displayProjects. Removing on success would open a
  //    render window where the cache has the old project but pendingDeleteIds no longer filters it.
  // Cleared only on: (a) undo button pressed, (b) delete API call failed.
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());

  const pendingDeleteTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const deletingInFlight = useRef<Set<string>>(new Set());
  // Tracks confirmed-deleted IDs for use inside callbacks (refs are always current; state closures
  // can be stale). Used when computing "remaining" projects to avoid switching to a deleted project
  // that a stale refetch may have temporarily re-added to the cache.
  const confirmedDeletedIdsRef = useRef<Set<string>>(new Set());
  const isCreatingBlankRef = useRef(false);
  // Read when the undo window ends, not at click time: the user may have switched or created a project meanwhile.
  const resolvedActiveIdRef = useRef(resolvedActiveId);
  resolvedActiveIdRef.current = resolvedActiveId;
  const callbacksRef = useRef({ onBeforeDelete, onSwitch, onCreateBlank, onLastDeleted });
  callbacksRef.current = { onBeforeDelete, onSwitch, onCreateBlank, onLastDeleted };
  const unmountContextRef = useRef({ klimFarmId, queryClient, t });
  unmountContextRef.current = { klimFarmId, queryClient, t };

  useEffect(function sendPendingDeletesOnUnmount() {
    const timers = pendingDeleteTimers.current;
    return () => {
      const { klimFarmId: farmId, queryClient: client, t: translate } = unmountContextRef.current;
      // Leaving the page ends the undo window: the toast said the project was deleted, so the delete is sent rather than dropped.
      timers.forEach((timer, projectId) => {
        clearTimeout(timer);
        deleteSoilSightProject(projectId)
          .then(() => client.setQueryData<SoilSightProject[]>([LIST_SOIL_SIGHT_PROJECTS, farmId], (old) => old?.filter((f) => f.id !== projectId)))
          .catch((saveError) => {
            client.invalidateQueries({ queryKey: [LIST_SOIL_SIGHT_PROJECTS, farmId] });
            showSoilSightSaveFailed(translate, saveError);
          });
      });
      timers.clear();
    };
  }, []);

  const handleDelete = useCallback(
    (projectId: string) => {
      const isActive = projectId === resolvedActiveId;

      // Hide immediately — no API call yet
      setPendingDeleteIds((prev) => new Set([...prev, projectId]));

      // FR3/AC4: Immediately switch to the next available project when the active one is deleted.
      let didSwitchImmediately = false;
      if (isActive) {
        const allProjects = queryClient.getQueryData<SoilSightProject[]>([LIST_SOIL_SIGHT_PROJECTS, klimFarmId]) ?? [];
        const allCurrentlyPending = new Set([projectId, ...pendingDeleteTimers.current.keys(), ...deletingInFlight.current]);
        const remaining = allProjects.filter((f) => !allCurrentlyPending.has(f.id) && !confirmedDeletedIdsRef.current.has(f.id));
        if (remaining.length > 0) {
          onSwitch(remaining[0].id);
          didSwitchImmediately = true;
        }
      }

      const toastKey = `undo-delete-${projectId}`;
      message.open({
        key: toastKey,
        duration: 4.5,
        content: (
          <span>
            {t("projectManagement.projectDeleted")}
            &nbsp;&nbsp;
            <button
              type="button"
              style={{ background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0, textDecoration: "underline" }}
              onClick={() => {
                message.destroy(toastKey);
                clearTimeout(pendingDeleteTimers.current.get(projectId));
                pendingDeleteTimers.current.delete(projectId);
                // Undo: remove from pendingDeleteIds so the project reappears.
                setPendingDeleteIds((prev) => {
                  const next = new Set(prev);
                  next.delete(projectId);
                  return next;
                });
              }}
            >
              {t("projectManagement.undoDelete")}
            </button>
          </span>
        ),
      });

      const timer = setTimeout(async () => {
        pendingDeleteTimers.current.delete(projectId);
        deletingInFlight.current.add(projectId);
        const { onBeforeDelete: beforeDelete, onSwitch: switchTo, onCreateBlank: createBlank, onLastDeleted: lastDeleted } = callbacksRef.current;
        // Still the shown project only if the user stayed on it; otherwise the page is already somewhere else.
        const isStillActive = !didSwitchImmediately && resolvedActiveIdRef.current === projectId;

        if (isStillActive) beforeDelete?.();

        try {
          await deleteSoilSightProject(projectId);
          // Mark in the ref so callbacks can exclude it from "remaining" checks even if a stale
          // refetch temporarily re-adds it to the cache (ref is always current; state is not).
          confirmedDeletedIdsRef.current = new Set([...confirmedDeletedIdsRef.current, projectId]);
          queryClient.setQueryData<SoilSightProject[]>([LIST_SOIL_SIGHT_PROJECTS, klimFarmId], (old) => old?.filter((f) => f.id !== projectId));
          // Intentionally NOT removing projectId from pendingDeleteIds here. It stays set for the
          // component's lifetime so displayProjects always filters it, even if a background refetch
          // (e.g. from a concurrent duplicate or create) returns stale data that includes it.
        } catch (saveError) {
          queryClient.invalidateQueries({ queryKey: [LIST_SOIL_SIGHT_PROJECTS, klimFarmId] });
          showSoilSightSaveFailed(t, saveError);
          // Delete failed — restore the project by removing it from the hidden set.
          setPendingDeleteIds((prev) => {
            const next = new Set(prev);
            next.delete(projectId);
            return next;
          });
          deletingInFlight.current.delete(projectId);
          return;
        }

        deletingInFlight.current.delete(projectId);

        const allRemaining = queryClient.getQueryData<SoilSightProject[]>([LIST_SOIL_SIGHT_PROJECTS, klimFarmId]) ?? [];
        const allPendingIds = new Set([...pendingDeleteTimers.current.keys(), ...deletingInFlight.current]);
        const remaining = allRemaining.filter((f) => !allPendingIds.has(f.id) && !confirmedDeletedIdsRef.current.has(f.id));
        productAnalytics.track("soilsights:project_delete", { project_count: remaining.length });

        if (isStillActive) {
          if (remaining.length === 0) {
            if (isCreatingBlankRef.current) return;
            isCreatingBlankRef.current = true;
            const allDeletingIds = [projectId, ...allPendingIds];
            try {
              await createBlank(allDeletingIds);
            } catch (saveError) {
              queryClient.invalidateQueries({ queryKey: [LIST_SOIL_SIGHT_PROJECTS, klimFarmId] });
              showSoilSightSaveFailed(t, saveError);
              lastDeleted?.();
            } finally {
              isCreatingBlankRef.current = false;
            }
          } else {
            switchTo(remaining[0].id);
          }
        }
      }, 5000);

      pendingDeleteTimers.current.set(projectId, timer);
    },
    [klimFarmId, onSwitch, queryClient, resolvedActiveId, t]
  );

  return { pendingDeleteIds, handleDelete };
};
